import { useState, MouseEvent, Fragment } from "react"
import { useTranslation } from "react-i18next"
import { ColorSwatches, SwatchesList } from "../Swatches"
import { Slider } from "../ui/Slider"
import { useEditor } from "@/utils/useEditor"
import { Adjustments } from "@/core/types"
import {
	HSLColorSwatches,
	HSLColors,
	getHueBackground,
	getLumBackground,
	getSatBackground,
} from "./utils"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "../ui/Select"
import clsx from "clsx"

type AdjustmentKey = keyof Adjustments

const hslColorKeys: Record<HSLColorSwatches, string> = {
	red: "hsl.colorRed",
	orange: "hsl.colorOrange",
	yellow: "hsl.colorYellow",
	green: "hsl.colorGreen",
	aqua: "hsl.colorAqua",
	blue: "hsl.colorBlue",
	purple: "hsl.colorPurple",
	magenta: "hsl.colorMagenta",
}

const hslKeyLabels: Record<HSLKey, string> = {
	hue: "hsl.keyHue",
	saturation: "hsl.keySaturation",
	luminance: "hsl.keyLuminance",
	all: "hsl.keyAll",
}

// Stable English names for history messages (never translated, mapped at display time)
const hslColorEnglish: Record<HSLColorSwatches, string> = {
	red: "Red",
	orange: "Orange",
	yellow: "Yellow",
	green: "Green",
	aqua: "Aqua",
	blue: "Blue",
	purple: "Purple",
	magenta: "Magenta",
}

const hslKeyEnglish: Record<string, string> = {
	hue: "Hue",
	saturation: "Saturation",
	luminance: "Luminance",
}

const hslKeyMap = {
	red: {
		hue: "hueRed",
		saturation: "saturationRed",
		luminance: "luminanceRed",
	},
	orange: {
		hue: "hueOrange",
		saturation: "saturationOrange",
		luminance: "luminanceOrange",
	},
	yellow: {
		hue: "hueYellow",
		saturation: "saturationYellow",
		luminance: "luminanceYellow",
	},
	green: {
		hue: "hueGreen",
		saturation: "saturationGreen",
		luminance: "luminanceGreen",
	},
	aqua: {
		hue: "hueAqua",
		saturation: "saturationAqua",
		luminance: "luminanceAqua",
	},
	blue: {
		hue: "hueBlue",
		saturation: "saturationBlue",
		luminance: "luminanceBlue",
	},
	purple: {
		hue: "huePurple",
		saturation: "saturationPurple",
		luminance: "luminancePurple",
	},
	magenta: {
		hue: "hueMagenta",
		saturation: "saturationMagenta",
		luminance: "luminanceMagenta",
	},
} satisfies Record<
	HSLColorSwatches,
	{ hue: AdjustmentKey; saturation: AdjustmentKey; luminance: AdjustmentKey }
>

type HSLAdjustMode = "Color" | "HSL"

const HSLKeyValues = ["hue", "saturation", "luminance", "all"] as const
type HSLKey = (typeof HSLKeyValues)[number]

export const HSL = (props: {
	values: Adjustments
	saveChange: (name: string, val: number) => void
	isMonochrome: boolean
}) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	/* Local state */

	const [selectedColorName, setSelectedColorName] = useState<HSLColorSwatches | "all">(
		HSLColors[0],
	)
	const [adjustMode, setAdjustMode] = useState<HSLAdjustMode>("HSL")

	const [hslKey, setHSLKey] = useState<HSLKey>("hue")

	/* Actions */

	const updateSelectedColorName = (_: MouseEvent<HTMLDivElement>, color: ColorSwatches) => {
		setSelectedColorName(color as HSLColorSwatches)
	}

	/* Rendering */

	const renderAdjustModeSelector = () => {
		return (
			<Select
				trackingContext="hsl"
				trackingLabel="hsl_adjust_selector"
				aria-label={t("hsl.ariaSelectHslAdjustmentMode")}
				value={adjustMode}
				onValueChange={(mode) => {
					setAdjustMode(mode as HSLAdjustMode)
				}}
			>
				<SelectTrigger>{t("hsl.adjustLabel", { mode: adjustMode })}</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						<SelectItem key="Color" value="Color">
							{t("hsl.color")}
						</SelectItem>
						<SelectItem key="HSL" value="HSL">
							{t("hsl.hsl")}
						</SelectItem>
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderColorSlider = (color: HSLColorSwatches, key: HSLKey, title: string) => {
		const background = () => {
			switch (key) {
				case "hue":
					return getHueBackground(color)
				case "saturation":
					return getSatBackground(color)
				case "luminance":
					return getLumBackground(color)
			}
		}
		return (
			<Slider
				key={`${color}-${key}`}
				title={title}
				panelName={t("hsl.panelHsl")}
				sectionName={`${t(hslColorKeys[color])} ${t(hslKeyLabels[key])}`}
				focusKey={hslKeyMap[color][key]}
				background={background()}
				keyboardStep={1}
				shiftKeyboardStep={10}
				hoverKeyboardStep={5}
				hoverShiftKeyboardStep={20}
				value={props.values[hslKeyMap[color][key]]}
				onUpdate={(val) => editor.setAdjustments({ [hslKeyMap[color][key]]: val })}
				onUpdateEnded={(_, displayedValue) =>
					props.saveChange(
						`${hslColorEnglish[color]} ${hslKeyEnglish[key]}`,
						displayedValue,
					)
				}
			/>
		)
	}

	const renderColorSliders = () => {
		return (
			<>
				<div className="w-full overflow-hidden">
					<SwatchesList
						colors={(HSLColors as unknown as ColorSwatches[]).concat(["all"])}
						selected={selectedColorName}
						onChange={updateSelectedColorName}
					/>
				</div>
				<div className="relative mt-3 w-full">
					{selectedColorName !== "all" && (
						<>
							{renderColorSlider(selectedColorName, "hue", t("hsl.keyHue"))}
							{renderColorSlider(
								selectedColorName,
								"saturation",
								t("hsl.keySaturation"),
							)}
							{renderColorSlider(
								selectedColorName,
								"luminance",
								t("hsl.keyLuminance"),
							)}
						</>
					)}
					{selectedColorName === "all" && (
						<div className="flex flex-col space-y-2">
							{HSLColors.map((color) => (
								<div
									className={clsx(
										color !== HSLColors.at(-1) &&
											"border-b border-base-700 pb-1",
										"flex flex-col items-end space-y-0",
									)}
									key={color}
								>
									<div className="pr-[191px] text-xs">
										{t(hslColorKeys[color])}
									</div>
									{renderColorSlider(color, "hue", t("hsl.keyHue"))}
									{renderColorSlider(color, "saturation", t("hsl.keySaturation"))}
									{renderColorSlider(color, "luminance", t("hsl.keyLuminance"))}
								</div>
							))}
						</div>
					)}
				</div>
			</>
		)
	}

	const renderHSLSliders = () => {
		return (
			<div className="flex flex-col space-y-3">
				<div className="flex w-full flex-row justify-center pt-1">
					{HSLKeyValues.map((key) => (
						<div
							key={key}
							className={clsx(
								hslKey === key ? "text-base-50" : "text-base-400",
								key !== "all" && "border-r border-base-700",
								"px-2 text-xs",
							)}
							onClick={() => setHSLKey(key)}
						>
							{t(hslKeyLabels[key])}
						</div>
					))}
				</div>

				{hslKey !== "all" && (
					<div className={clsx("flex flex-col items-end space-y-0")}>
						{HSLColors.map((color) => (
							<Fragment key={color}>
								{renderColorSlider(color, hslKey, t(hslColorKeys[color]))}
							</Fragment>
						))}
					</div>
				)}

				{hslKey === "all" && (
					<div className="flex flex-col space-y-2">
						{(["hue", "saturation", "luminance"] as HSLKey[]).map((key) => (
							<div
								className={clsx(
									key !== "luminance" && "border-b border-base-700 pb-1",
									"flex flex-col items-end space-y-0",
								)}
								key={key}
							>
								<div className="pr-[191px] text-xs">{t(hslKeyLabels[key])}</div>
								{HSLColors.map((color) => (
									<Fragment key={color}>
										{renderColorSlider(color, key, t(hslColorKeys[color]))}
									</Fragment>
								))}
							</div>
						))}
					</div>
				)}
			</div>
		)
	}

	const renderBWSliders = () => {
		return (
			<>
				<div className="relative mt-3 w-full">
					<div className="flex flex-col space-y-2">
						<div className="ml-[68px] w-full text-xs">{t("hsl.blackWhiteMix")}</div>
						<div className="flex flex-col space-y-0">
							{HSLColors.map((color) => (
								<Fragment key={color}>
									{renderColorSlider(color, "luminance", t(hslColorKeys[color]))}
								</Fragment>
							))}
						</div>
					</div>
				</div>
			</>
		)
	}

	return (
		<div className="box-border flex w-full select-none flex-col">
			{props.isMonochrome && renderBWSliders()}
			{!props.isMonochrome && (
				<>
					{renderAdjustModeSelector()}
					{adjustMode === "Color" && renderColorSliders()}
					{adjustMode === "HSL" && renderHSLSliders()}
				</>
			)}
		</div>
	)
}
